import React, { useRef, useState, useEffect } from 'react';
import TeamMember from '../ui/TeamMember';
import teamMembers from '../../data/team';

const TeamSection = () => {
  const carouselRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const teamMembersRef = useRef([]);

  const scrollTeamCarousel = () => {
    if (carouselRef.current) {
      const scrollAmount = 350;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
      setShowScrollIndicator(!isAtEnd);
    }
  };

  const alignTeamMembers = () => {
    const members = teamMembersRef.current;
    if (members.length === 0) return;

    members.forEach(member => {
      const teamCard = member?.querySelector('.team-member');
      const infoContainer = member?.querySelector('.team-member-info');
      const nameContainer = member?.querySelector('.team-member-name-container');
      const positionContainer = member?.querySelector('.team-member-position-container');

      if (teamCard) teamCard.style.height = 'auto';
      if (infoContainer) infoContainer.style.height = 'auto';
      if (nameContainer) nameContainer.style.height = 'auto';
      if (positionContainer) positionContainer.style.height = 'auto';
    });

    setTimeout(() => {
      let maxInfoHeight = 0;
      let maxCardHeight = 0;
      let maxPositionHeight = 0;

      members.forEach(member => {
        const teamCard = member?.querySelector('.team-member');
        const infoContainer = member?.querySelector('.team-member-info');
        const positionContainer = member?.querySelector('.team-member-position-container');

        if (teamCard) maxCardHeight = Math.max(maxCardHeight, teamCard.offsetHeight);
        if (infoContainer) maxInfoHeight = Math.max(maxInfoHeight, infoContainer.offsetHeight);
        if (positionContainer) maxPositionHeight = Math.max(maxPositionHeight, positionContainer.offsetHeight);
      });

      members.forEach(member => {
        const teamCard = member?.querySelector('.team-member');
        const infoContainer = member?.querySelector('.team-member-info');
        const positionContainer = member?.querySelector('.team-member-position-container');

        if (teamCard) teamCard.style.height = `${maxCardHeight}px`;
        if (infoContainer) infoContainer.style.height = `${maxInfoHeight}px`;
        if (positionContainer) positionContainer.style.height = `${maxPositionHeight}px`;
      });
    }, 10);
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
      return () => {
        carousel.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      alignTeamMembers();
    }, 100);

    const handleResize = () => {
      setTimeout(alignTeamMembers, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const values = [
    {
      icon: '💡',
      title: "Innovation",
      description: "Embrace new ideas and technologies. We constantly refine our models and methods to stay at the forefront of AI and materials science, delivering state-of-the-art solutions to users."
    },
    {
      icon: '🎓',
      title: "Expertise",
      description: "Value deep metallurgical knowledge and data science know-how. Our team - from PhD metallurgists to machine learning engineers - shares a passion for learning and scientific rigor."
    },
    {
      icon: '🤝',
      title: "Collaboration",
      description: "Work together for the best results. We operate as a tight-knit, interdisciplinary team internally, and we partner closely with clients, research institutions, and industry groups externally."
    },
    {
      icon: '🔒',
      title: "Integrity",
      description: "Commitment to trust, honesty, quality, and reliability. From ensuring data confidentiality to providing accurate predictions, we conduct our business with professionalism."
    },
    {
      icon: '🌱',
      title: "Sustainability",
      description: "Drive toward sustainable steel development. Reducing waste and energy use in steelmaking isn’t just a selling point - it’s a responsibility we take seriously. We align our projects with sustainability objectives for the betterment of industry and society."
    }
  ];

  return (
    <section className="team-section" id="team">
      <div className="value-container">
        {/* Vision & Mission Block */}
        <div className="section-block vision-mission-block" id="about">
          <div className="mission-hero">
            <div className="mission-badge">
              <span className="section-label">About Ferritico</span>
            </div>
            <h2 className="section-title">About and Team</h2>
            <div className="mission-accent"></div>
          </div>

          <div className="mission-cards-container">

            <div className="mission-card mission-card">
              <div className="mission-card-inner">
                <div className="icon-wrapper">
                  <i class="fi fi-ss-flag"></i>
                  <div className="icon-bg"></div>
                </div>
                <div className="mission-content">
                  <h3 className="mission-card-title">Mission</h3>
                  <p className="mission-card-text">
                    Equip steel engineers and manufacturers with digital metallurgy tools that accelerate innovation while reducing cost and environmental impact.
                  </p>
                </div>
              </div>
            </div>

            <div className="mission-card vision-card">
              <div className="mission-card-inner">
                <div className="icon-wrapper">
                  <i class="fi fi-sr-crystal-ball"></i>
                  <div className="icon-bg"></div>
                </div>
                <div className="mission-content">
                  <h3 className="mission-card-title">Vision</h3>
                  <p className="mission-card-text">
                    Revolutionize steel development through AI and data-driven insight, making alloy design and process optimization faster, smarter, and more sustainable.
                  </p>
                </div>
              </div>
            </div>

            <div className="mission-card vision-card">
              <div className="mission-card-inner">
                <div className="icon-wrapper">
                  <i class="fi fi-sr-search-alt"></i>
                  <div className="icon-bg"></div>
                </div>
                <div className="mission-content">
                  <h3 className="mission-card-title">Research Roots</h3>
                  <p className="mission-card-text">
                    Ferritico is born from cutting-edge research at KTH Royal Institute of Technology.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>


        <div className="section-block team-block" id="team-members">
          <div className="block-header">
            <h3 className="subsection-title">The Ferritico Team</h3>
            <p className="section-subtitle">
              Ferritico is built by a diverse team of experts across metallurgy, AI, engineering, and innovation.
            </p>
          </div>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                ref={el => teamMembersRef.current[index] = el}
              >
                <TeamMember
                  name={member.name}
                  position={member.position}
                  image={member.image}
                  email={member.email}
                  linkedin={member.linkedin}
                  delay={member.delay}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
